import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Users, Eye, 
  Search, RefreshCw, UserX, FileWarning, Activity, Clock,
  AlertOctagon, TrendingUp, Ban, ChevronRight, Filter,
  Fingerprint, X, Merge, AlertCircle, Gavel
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { FraudFlag } from '../types';
import { useToast } from '../contexts/ToastContext';
import { formatDistanceToNow } from 'date-fns';
import './Fraud.css';

interface FraudStat {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}

const Fraud: React.FC = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'overview' | 'flags' | 'duplicates' | 'integrity' | 'biokinetic' | 'appeals'>('overview');
  const [appeals, setAppeals] = useState<any[]>([]);
  const [appealsLoading, setAppealsLoading] = useState(false);
  const [appealStatusFilter, setAppealStatusFilter] = useState<string>('pending');
  const [appealModal, setAppealModal] = useState<{ open: boolean; appeal: any | null; approve: boolean; notes: string; loading: boolean }>({ open: false, appeal: null, approve: true, notes: '', loading: false });
  const [fraudFlags, setFraudFlags] = useState<FraudFlag[]>([]);
  const [duplicateReport, setDuplicateReport] = useState<any>(null);
  const [integrityData, setIntegrityData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal states
  const [banModal, setBanModal] = useState<{ open: boolean; flag: FraudFlag | null }>({ open: false, flag: null });
  const [detailModal, setDetailModal] = useState<{ open: boolean; flag: FraudFlag | null; riskAnalysis: any | null; loading: boolean }>({ open: false, flag: null, riskAnalysis: null, loading: false });
  const [duplicateModal, setDuplicateModal] = useState<{ open: boolean; userId: number | null; duplicates: any[] | null; loading: boolean }>({ open: false, userId: null, duplicates: null, loading: false });
  const [mergeModal, setMergeModal] = useState<{ open: boolean; primaryId: number | null; duplicateIds: number[]; loading: boolean }>({ open: false, primaryId: null, duplicateIds: [], loading: false });

  // Bio-kinetic state
  const [bioKineticData, setBioKineticData] = useState<{ config: any; stats: any; flaggedUsers: any[] }>({ config: null, stats: null, flaggedUsers: [] });
  const [bioKineticLoading, setBioKineticLoading] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    totalFlags: 0,
    pendingFlags: 0,
    criticalFlags: 0,
    resolvedToday: 0,
    riskScore: 0,
  });

  const loadFraudFlags = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getFraudFlags();
      setFraudFlags(data || []);
      
      const pending = (data || []).filter((f: FraudFlag) => !f.is_resolved);
      const critical = (data || []).filter((f: FraudFlag) => f.severity === 'critical');
      const resolvedToday = (data || []).filter((f: FraudFlag) => {
        if (!f.resolved_at) return false;
        const resolved = new Date(f.resolved_at);
        const today = new Date();
        return resolved.toDateString() === today.toDateString();
      });
      
      setStats({
        totalFlags: (data || []).length,
        pendingFlags: pending.length,
        criticalFlags: critical.length,
        resolvedToday: resolvedToday.length,
        riskScore: Math.min(100, pending.length * 10 + critical.length * 25),
      });
    } catch (error) {
      console.error('Failed to load fraud flags:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDuplicateReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getDuplicateReport();
      setDuplicateReport(data);
    } catch (error) {
      console.error('Failed to load duplicate report:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBioKineticData = useCallback(async () => {
    try {
      setBioKineticLoading(true);
      const [config, fraudStats, flaggedUsers] = await Promise.all([
        adminService.getFraudConfig(),
        adminService.getFraudStats(24),
        adminService.getFlaggedUsers({ hours: 24, limit: 50 }),
      ]);
      setBioKineticData({ config, stats: fraudStats, flaggedUsers: flaggedUsers || [] });
    } catch (error) {
      console.error('Failed to load bio-kinetic data:', error);
      toast.error('Failed to load bio-kinetic data');
    } finally {
      setBioKineticLoading(false);
    }
  }, [toast]);

  const loadAppeals = useCallback(async () => {
    try {
      setAppealsLoading(true);
      const data = await adminService.getFraudAppeals({
        status: appealStatusFilter === 'all' ? undefined : appealStatusFilter,
        limit: 200,
      });
      setAppeals(data?.appeals || []);
    } catch (error) {
      console.error('Failed to load appeals:', error);
      toast.error('Failed to load appeals');
    } finally {
      setAppealsLoading(false);
    }
  }, [appealStatusFilter, toast]);

  const handleResolveAppeal = async () => {
    if (!appealModal.appeal) return;
    try {
      setAppealModal((m) => ({ ...m, loading: true }));
      await adminService.resolveFraudAppeal(appealModal.appeal.id, appealModal.approve, appealModal.notes || undefined);
      toast.success(`Appeal ${appealModal.approve ? 'approved' : 'denied'}.`);
      setAppealModal({ open: false, appeal: null, approve: true, notes: '', loading: false });
      loadAppeals();
    } catch (error) {
      console.error('Failed to resolve appeal:', error);
      toast.error('Failed to resolve appeal');
      setAppealModal((m) => ({ ...m, loading: false }));
    }
  };

  useEffect(() => {
    loadFraudFlags();
    if (activeTab === 'duplicates') {
      loadDuplicateReport();
    }
    if (activeTab === 'biokinetic') {
      loadBioKineticData();
    }
    if (activeTab === 'appeals') {
      loadAppeals();
    }
  }, [activeTab, loadFraudFlags, loadDuplicateReport, loadBioKineticData, loadAppeals]);

  const handleResolveFlag = async (flagId: number) => {
    if (!window.confirm('Are you sure you want to mark this flag as resolved?')) return;
    
    try {
      await adminService.resolveFraudFlag(flagId);
      toast.success('Flag resolved', 'The fraud flag has been marked as resolved.');
      await loadFraudFlags();
    } catch (error) {
      toast.error('Failed to resolve fraud flag');
    }
  };

  const handleRunFraudScan = async () => {
    try {
      setLoading(true);
      const result = await adminService.runFraudScan();
      toast.success('Scan complete', `Found ${result?.flagged_count || 0} new issues.`);
      await loadFraudFlags();
    } catch (error) {
      toast.error('Failed to run fraud scan');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUserWallet = async () => {
    if (!selectedUserId) {
      toast.warning('Please enter a user ID');
      return;
    }

    try {
      setLoading(true);
      const result = await adminService.verifyUserWallet(parseInt(selectedUserId));
      setIntegrityData(result);
    } catch (error) {
      toast.error('Failed to verify user wallet');
    } finally {
      setLoading(false);
    }
  };

  // Ban user handler
  const handleBanUser = async () => {
    if (!banModal.flag) return;
    try {
      await adminService.updateUserStatus(banModal.flag.user_id, false);
      toast.success('User banned', `User #${banModal.flag.user_id} has been deactivated.`);
      setBanModal({ open: false, flag: null });
      await loadFraudFlags();
    } catch (error) {
      toast.error('Failed to ban user', 'An error occurred while deactivating the user.');
    }
  };

  // View details handler
  const handleViewDetails = async (flag: FraudFlag) => {
    setDetailModal({ open: true, flag, riskAnalysis: null, loading: true });
    try {
      const analysis = await adminService.analyzeUserFraudRisk(flag.user_id);
      setDetailModal(prev => ({ ...prev, riskAnalysis: analysis, loading: false }));
    } catch (error) {
      setDetailModal(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load risk analysis');
    }
  };

  // Duplicate detection handlers
  const handleViewDuplicates = async (userId: number) => {
    setDuplicateModal({ open: true, userId, duplicates: null, loading: true });
    try {
      const data = await adminService.getUserDuplicates(userId);
      setDuplicateModal(prev => ({ ...prev, duplicates: Array.isArray(data) ? data : data?.duplicates || [], loading: false }));
    } catch (error) {
      setDuplicateModal(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load duplicates');
    }
  };

  const handleMergeUsers = async () => {
    if (!mergeModal.primaryId || mergeModal.duplicateIds.length === 0) return;
    try {
      setMergeModal(prev => ({ ...prev, loading: true }));
      await adminService.mergeUsers(mergeModal.primaryId!, mergeModal.duplicateIds);
      toast.success('Users merged', `${mergeModal.duplicateIds.length} duplicate account(s) merged into User #${mergeModal.primaryId}.`);
      setMergeModal({ open: false, primaryId: null, duplicateIds: [], loading: false });
      setDuplicateModal({ open: false, userId: null, duplicates: null, loading: false });
      await loadDuplicateReport();
    } catch (error) {
      setMergeModal(prev => ({ ...prev, loading: false }));
      toast.error('Failed to merge users', 'An error occurred during the merge operation.');
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertOctagon size={16} />;
      case 'high': return <AlertTriangle size={16} />;
      case 'medium': return <FileWarning size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const filteredFlags = fraudFlags.filter(flag => {
    const matchesSearch = searchQuery === '' || 
      flag.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      flag.flag_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      flag.user_id.toString().includes(searchQuery);
    
    const matchesSeverity = severityFilter === 'all' || flag.severity === severityFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'pending' && !flag.is_resolved) ||
      (statusFilter === 'resolved' && flag.is_resolved);
    
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const statCards: FraudStat[] = [
    { label: 'Total Flags', value: stats.totalFlags, icon: <Shield size={24} />, color: '#410C6D' },
    { label: 'Pending Review', value: stats.pendingFlags, icon: <Clock size={24} />, color: '#f59e0b' },
    { label: 'Critical', value: stats.criticalFlags, icon: <AlertOctagon size={24} />, color: '#ef4444' },
    { label: 'Resolved Today', value: stats.resolvedToday, icon: <CheckCircle size={24} />, color: '#22c55e' },
  ];

  return (
    <div className="fraud-page-modern">
      {/* Header */}
      <div className="fraud-header">
        <div className="header-content">
          <h1><Shield size={28} /> Fraud Detection</h1>
          <p className="header-subtitle">Monitor and manage security threats</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => loadFraudFlags()} 
            className="refresh-btn"
            disabled={loading}
          >
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
          <button 
            onClick={handleRunFraudScan} 
            className="scan-btn"
            disabled={loading}
          >
            <Search size={18} />
            {loading ? 'Scanning...' : 'Run Full Scan'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="fraud-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          <Activity size={18} /> Overview
        </button>
        <button
          className={activeTab === 'flags' ? 'active' : ''}
          onClick={() => setActiveTab('flags')}
        >
          <AlertTriangle size={18} /> Fraud Flags
          {stats.pendingFlags > 0 && (
            <span className="badge">{stats.pendingFlags}</span>
          )}
        </button>
        <button
          className={activeTab === 'duplicates' ? 'active' : ''}
          onClick={() => setActiveTab('duplicates')}
        >
          <Users size={18} /> Duplicate Detection
        </button>
        <button
          className={activeTab === 'integrity' ? 'active' : ''}
          onClick={() => setActiveTab('integrity')}
        >
          <Eye size={18} /> Transaction Integrity
        </button>
        <button
          className={activeTab === 'biokinetic' ? 'active' : ''}
          onClick={() => setActiveTab('biokinetic')}
        >
          <Fingerprint size={18} /> Bio-Kinetic
        </button>
        <button
          className={activeTab === 'appeals' ? 'active' : ''}
          onClick={() => setActiveTab('appeals')}
        >
          <Gavel size={18} /> Appeals
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="fraud-overview">
          <div className="stats-row">
            {statCards.map((stat, index) => (
              <div 
                key={index} 
                className="stat-card"
                style={{ borderLeftColor: stat.color }}
              >
                <div className="stat-icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                  {stat.icon}
                </div>
                <div className="stat-content">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="risk-score-card">
            <h3>Platform Risk Score</h3>
            <div className="risk-meter">
              <div 
                className="risk-bar"
                style={{ 
                  width: `${stats.riskScore}%`,
                  backgroundColor: stats.riskScore > 70 ? '#ef4444' : stats.riskScore > 40 ? '#f59e0b' : '#22c55e'
                }}
              />
            </div>
            <div className="risk-labels">
              <span>Low</span>
              <span className="risk-value">{stats.riskScore}%</span>
              <span>High</span>
            </div>
            <p className="risk-description">
              {stats.riskScore > 70 
                ? 'High risk detected. Immediate action required.'
                : stats.riskScore > 40 
                  ? 'Moderate risk. Review pending flags.'
                  : 'Low risk. Platform security is healthy.'}
            </p>
          </div>

          <div className="critical-flags-card">
            <div className="card-header">
              <h3><AlertOctagon size={20} /> Recent Critical Alerts</h3>
              <button onClick={() => setActiveTab('flags')} className="view-all">
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="critical-list">
              {fraudFlags.filter(f => f.severity === 'critical' && !f.is_resolved).slice(0, 5).map(flag => (
                <div key={flag.id} className="critical-item">
                  <div className="critical-icon">
                    <AlertOctagon size={20} />
                  </div>
                  <div className="critical-content">
                    <span className="critical-type">{flag.flag_type}</span>
                    <span className="critical-desc">{flag.description}</span>
                    <span className="critical-time">
                      <Clock size={12} />
                      {formatDistanceToNow(new Date(flag.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <button 
                    onClick={() => handleResolveFlag(flag.id)}
                    className="resolve-quick-btn"
                  >
                    Resolve
                  </button>
                </div>
              ))}
              {fraudFlags.filter(f => f.severity === 'critical' && !f.is_resolved).length === 0 && (
                <div className="no-critical">
                  <CheckCircle size={24} />
                  <span>No critical alerts</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Flags Tab */}
      {activeTab === 'flags' && (
        <div className="fraud-content">
          <div className="filters-bar">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search flags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <Filter size={16} />
              <select 
                value={severityFilter} 
                onChange={(e) => setSeverityFilter(e.target.value)}
              >
                <option value="all">All Severity</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <span>Loading fraud flags...</span>
            </div>
          ) : (
            <div className="flags-table-container">
              <table className="fraud-table">
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>User</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFlags.map((flag) => (
                    <tr key={flag.id} className={`severity-${flag.severity}`}>
                      <td>
                        <span className={`severity-badge ${flag.severity}`}>
                          {getSeverityIcon(flag.severity)}
                          {flag.severity}
                        </span>
                      </td>
                      <td>
                        <a href={`/dashboard/users/${flag.user_id}`} className="user-link">
                          #{flag.user_id}
                        </a>
                      </td>
                      <td><span className="flag-type">{flag.flag_type}</span></td>
                      <td className="description-cell">{flag.description}</td>
                      <td>
                        <span className={`status-badge ${flag.is_resolved ? 'resolved' : 'pending'}`}>
                          {flag.is_resolved ? (
                            <><CheckCircle size={14} /> Resolved</>
                          ) : (
                            <><Clock size={14} /> Pending</>
                          )}
                        </span>
                      </td>
                      <td className="date-cell">
                        {formatDistanceToNow(new Date(flag.created_at), { addSuffix: true })}
                      </td>
                      <td>
                        <div className="action-buttons">
                          {!flag.is_resolved && (
                            <>
                              <button
                                onClick={() => handleResolveFlag(flag.id)}
                                className="action-btn resolve"
                                title="Mark as resolved"
                              >
                                <CheckCircle size={16} />
                              </button>
                              <button
                                onClick={() => setBanModal({ open: true, flag })}
                                className="action-btn ban"
                                title="Ban user"
                              >
                                <Ban size={16} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleViewDetails(flag)}
                            className="action-btn view"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredFlags.length === 0 && (
                <div className="no-data">
                  <Shield size={48} />
                  <h3>No fraud flags found</h3>
                  <p>No flags match your current filters</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Duplicates Tab */}
      {activeTab === 'duplicates' && (
        <div className="fraud-content">
          <div className="duplicate-section">
            <div className="section-header">
              <h3><Users size={20} /> Duplicate Account Detection</h3>
              <p>Identify users with multiple accounts based on device, phone, or email patterns</p>
            </div>
            
            {loading ? (
              <div className="loading-state">
                <RefreshCw className="spin" size={32} />
                <span>Loading duplicate report...</span>
              </div>
            ) : duplicateReport ? (
              <div className="duplicate-results">
                <div className="duplicate-summary">
                  <div className="summary-item">
                    <UserX size={24} />
                    <span className="summary-value">{duplicateReport?.total_duplicates || 0}</span>
                    <span className="summary-label">Duplicate Accounts</span>
                  </div>
                  <div className="summary-item">
                    <TrendingUp size={24} />
                    <span className="summary-value">{duplicateReport?.risk_clusters || 0}</span>
                    <span className="summary-label">Risk Clusters</span>
                  </div>
                </div>

                {/* Cluster list with actionable buttons */}
                {duplicateReport?.clusters && Array.isArray(duplicateReport.clusters) && duplicateReport.clusters.length > 0 ? (
                  <div className="duplicate-clusters">
                    <h4 className="clusters-title">Detected Clusters</h4>
                    {duplicateReport.clusters.map((cluster: any, idx: number) => (
                      <div key={idx} className="cluster-card">
                        <div className="cluster-header">
                          <span className="cluster-label">
                            <Users size={16} /> Cluster #{idx + 1}
                          </span>
                          <span className="cluster-count">{cluster.user_ids?.length || cluster.users?.length || 0} accounts</span>
                        </div>
                        <div className="cluster-users">
                          {(cluster.user_ids || cluster.users || []).map((userId: any) => {
                            const uid = typeof userId === 'object' ? userId.id || userId.user_id : userId;
                            return (
                              <div key={uid} className="cluster-user-row">
                                <a href={`/dashboard/users/${uid}`} className="user-link">User #{uid}</a>
                                <button
                                  onClick={() => handleViewDuplicates(uid)}
                                  className="cluster-action-btn"
                                >
                                  <Eye size={14} /> View Duplicates
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <pre className="report-data">{JSON.stringify(duplicateReport, null, 2)}</pre>
                )}
              </div>
            ) : (
              <div className="no-data">
                <Users size={48} />
                <h3>No duplicate data</h3>
                <p>Run a scan to detect duplicate accounts</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Integrity Tab */}
      {activeTab === 'integrity' && (
        <div className="fraud-content">
          <div className="integrity-section">
            <div className="section-header">
              <h3><Eye size={20} /> Transaction Integrity Check</h3>
              <p>Verify user wallet balances and transaction history</p>
            </div>
            
            <div className="verify-form">
              <input
                type="number"
                placeholder="Enter User ID"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="user-id-input"
              />
              <button 
                onClick={handleVerifyUserWallet} 
                className="verify-btn"
                disabled={loading || !selectedUserId}
              >
                {loading ? <RefreshCw className="spin" size={18} /> : <Search size={18} />}
                Verify Wallet
              </button>
            </div>

            {integrityData && (
              <div className="integrity-result">
                <div className="result-header">
                  <h4>Verification Result for User #{selectedUserId}</h4>
                  <span className={`integrity-status ${integrityData.is_valid ? 'valid' : 'invalid'}`}>
                    {integrityData.is_valid ? (
                      <><CheckCircle size={16} /> Valid</>
                    ) : (
                      <><XCircle size={16} /> Issues Found</>
                    )}
                  </span>
                </div>
                <pre className="result-data">{JSON.stringify(integrityData, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bio-Kinetic Tab */}
      {activeTab === 'biokinetic' && (
        <div className="fraud-content">
          <div className="biokinetic-section">
            <div className="section-header">
              <h3><Fingerprint size={20} /> Bio-Kinetic Fraud Detection</h3>
              <p>Behavioral biometrics analysis — gyroscope, tap patterns, and device motion signals</p>
            </div>

            {bioKineticLoading ? (
              <div className="loading-state">
                <RefreshCw className="spin" size={32} />
                <span>Loading bio-kinetic data...</span>
              </div>
            ) : (
              <>
                {/* Config summary */}
                {bioKineticData.config && (
                  <div className="bk-config-card">
                    <h4><Activity size={16} /> Detection Configuration</h4>
                    <div className="bk-config-grid">
                      {Object.entries(bioKineticData.config).map(([key, value]) => (
                        <div key={key} className="bk-config-item">
                          <span className="bk-config-key">{key.replace(/_/g, ' ')}</span>
                          <span className="bk-config-value">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stats cards */}
                {bioKineticData.stats && (
                  <div className="bk-stats-row">
                    {[
                      { label: 'Total Analyzed', value: bioKineticData.stats.total_analyzed ?? bioKineticData.stats.total ?? 0, color: '#410C6D' },
                      { label: 'Flagged', value: bioKineticData.stats.flagged ?? bioKineticData.stats.total_flagged ?? 0, color: '#f59e0b' },
                      { label: 'Shadow Banned', value: bioKineticData.stats.shadowbanned ?? bioKineticData.stats.shadow_banned ?? 0, color: '#ef4444' },
                      { label: 'Clean', value: bioKineticData.stats.clean ?? bioKineticData.stats.total_clean ?? 0, color: '#22c55e' },
                    ].map((s, i) => (
                      <div key={i} className="bk-stat-card" style={{ borderLeftColor: s.color }}>
                        <span className="bk-stat-value" style={{ color: s.color }}>{s.value}</span>
                        <span className="bk-stat-label">{s.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Flagged users table */}
                {bioKineticData.flaggedUsers.length > 0 ? (
                  <div className="bk-table-container">
                    <h4 className="bk-table-title">
                      <AlertTriangle size={16} /> Flagged Users (Last 24h)
                    </h4>
                    <table className="fraud-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Gyro Variance</th>
                          <th>Tap Variance</th>
                          <th>Confidence</th>
                          <th>Action Taken</th>
                          <th>Flagged At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bioKineticData.flaggedUsers.map((user: any, idx: number) => (
                          <tr key={user.user_id || idx}>
                            <td>
                              <a href={`/dashboard/users/${user.user_id}`} className="user-link">
                                #{user.user_id}
                              </a>
                            </td>
                            <td>
                              <span className="bk-metric">{user.gyro_variance?.toFixed(4) ?? user.gyroscope_variance?.toFixed(4) ?? '—'}</span>
                            </td>
                            <td>
                              <span className="bk-metric">{user.tap_variance?.toFixed(4) ?? user.tap_pattern_variance?.toFixed(4) ?? '—'}</span>
                            </td>
                            <td>
                              <span className={`bk-confidence ${(user.confidence_score ?? user.confidence ?? 0) > 0.8 ? 'high' : (user.confidence_score ?? user.confidence ?? 0) > 0.5 ? 'medium' : 'low'}`}>
                                {((user.confidence_score ?? user.confidence ?? 0) * 100).toFixed(1)}%
                              </span>
                            </td>
                            <td>
                              <span className={`bk-action-badge ${user.action_taken || user.action || 'none'}`}>
                                {user.action_taken || user.action || 'none'}
                              </span>
                            </td>
                            <td className="date-cell">
                              {user.flagged_at || user.created_at
                                ? formatDistanceToNow(new Date(user.flagged_at || user.created_at), { addSuffix: true })
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">
                    <Fingerprint size={48} />
                    <h3>No flagged users</h3>
                    <p>No bio-kinetic fraud signals detected in the last 24 hours</p>
                  </div>
                )}

                <button onClick={loadBioKineticData} className="refresh-btn" style={{ marginTop: 16 }} disabled={bioKineticLoading}>
                  <RefreshCw size={16} className={bioKineticLoading ? 'spin' : ''} />
                  Refresh Bio-Kinetic Data
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ========== MODALS ========== */}

      {/* Ban User Confirmation Modal */}
      {banModal.open && banModal.flag && (
        <div className="fraud-modal-overlay" onClick={() => setBanModal({ open: false, flag: null })}>
          <div className="fraud-modal" onClick={e => e.stopPropagation()}>
            <div className="fraud-modal-header danger">
              <Ban size={22} />
              <h3>Ban User</h3>
              <button className="modal-close-btn" onClick={() => setBanModal({ open: false, flag: null })}>
                <X size={18} />
              </button>
            </div>
            <div className="fraud-modal-body">
              <div className="ban-warning">
                <AlertCircle size={40} />
                <p>Are you sure you want to <strong>ban User #{banModal.flag.user_id}</strong>?</p>
                <p className="ban-warning-sub">This will deactivate the user account. This action can be reversed from the Users page.</p>
              </div>
              <div className="ban-details">
                <div className="ban-detail-row">
                  <span className="ban-detail-label">Flag Type</span>
                  <span className="ban-detail-value">{banModal.flag.flag_type}</span>
                </div>
                <div className="ban-detail-row">
                  <span className="ban-detail-label">Severity</span>
                  <span className={`severity-badge ${banModal.flag.severity}`}>
                    {getSeverityIcon(banModal.flag.severity)} {banModal.flag.severity}
                  </span>
                </div>
                <div className="ban-detail-row">
                  <span className="ban-detail-label">Description</span>
                  <span className="ban-detail-value">{banModal.flag.description}</span>
                </div>
              </div>
            </div>
            <div className="fraud-modal-footer">
              <button className="modal-cancel-btn" onClick={() => setBanModal({ open: false, flag: null })}>
                Cancel
              </button>
              <button className="modal-danger-btn" onClick={handleBanUser}>
                <Ban size={16} /> Ban User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {detailModal.open && detailModal.flag && (
        <div className="fraud-modal-overlay" onClick={() => setDetailModal({ open: false, flag: null, riskAnalysis: null, loading: false })}>
          <div className="fraud-modal fraud-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="fraud-modal-header">
              <Eye size={22} />
              <h3>Flag Details</h3>
              <button className="modal-close-btn" onClick={() => setDetailModal({ open: false, flag: null, riskAnalysis: null, loading: false })}>
                <X size={18} />
              </button>
            </div>
            <div className="fraud-modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Flag Type</span>
                  <span className="flag-type">{detailModal.flag.flag_type}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Severity</span>
                  <span className={`severity-badge ${detailModal.flag.severity}`}>
                    {getSeverityIcon(detailModal.flag.severity)} {detailModal.flag.severity}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">User</span>
                  <a href={`/dashboard/users/${detailModal.flag.user_id}`} className="user-link">
                    #{detailModal.flag.user_id}
                  </a>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${detailModal.flag.is_resolved ? 'resolved' : 'pending'}`}>
                    {detailModal.flag.is_resolved ? <><CheckCircle size={14} /> Resolved</> : <><Clock size={14} /> Pending</>}
                  </span>
                </div>
                <div className="detail-item full-width">
                  <span className="detail-label">Description</span>
                  <span className="detail-description">{detailModal.flag.description}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Created</span>
                  <span>{new Date(detailModal.flag.created_at).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Resolved</span>
                  <span>{detailModal.flag.resolved_at ? new Date(detailModal.flag.resolved_at).toLocaleString() : '—'}</span>
                </div>
              </div>

              <div className="risk-analysis-section">
                <h4><TrendingUp size={16} /> Fraud Risk Analysis</h4>
                {detailModal.loading ? (
                  <div className="loading-state" style={{ padding: '24px' }}>
                    <RefreshCw className="spin" size={24} />
                    <span>Analyzing fraud risk...</span>
                  </div>
                ) : detailModal.riskAnalysis ? (
                  <div className="risk-analysis-content">
                    {detailModal.riskAnalysis.risk_score !== undefined && (
                      <div className="risk-analysis-score">
                        <span className="risk-analysis-label">Risk Score</span>
                        <div className="risk-meter" style={{ marginBottom: 8 }}>
                          <div 
                            className="risk-bar"
                            style={{ 
                              width: `${Math.min(100, detailModal.riskAnalysis.risk_score)}%`,
                              backgroundColor: detailModal.riskAnalysis.risk_score > 70 ? '#ef4444' : detailModal.riskAnalysis.risk_score > 40 ? '#f59e0b' : '#22c55e'
                            }}
                          />
                        </div>
                        <span className="risk-score-number">{detailModal.riskAnalysis.risk_score}%</span>
                      </div>
                    )}
                    <pre className="result-data">{JSON.stringify(detailModal.riskAnalysis, null, 2)}</pre>
                  </div>
                ) : (
                  <p className="no-analysis">Risk analysis data unavailable.</p>
                )}
              </div>
            </div>
            <div className="fraud-modal-footer">
              <button className="modal-cancel-btn" onClick={() => setDetailModal({ open: false, flag: null, riskAnalysis: null, loading: false })}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Users Modal */}
      {duplicateModal.open && (
        <div className="fraud-modal-overlay" onClick={() => setDuplicateModal({ open: false, userId: null, duplicates: null, loading: false })}>
          <div className="fraud-modal fraud-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="fraud-modal-header">
              <Users size={22} />
              <h3>Duplicates for User #{duplicateModal.userId}</h3>
              <button className="modal-close-btn" onClick={() => setDuplicateModal({ open: false, userId: null, duplicates: null, loading: false })}>
                <X size={18} />
              </button>
            </div>
            <div className="fraud-modal-body">
              {duplicateModal.loading ? (
                <div className="loading-state" style={{ padding: '24px' }}>
                  <RefreshCw className="spin" size={24} />
                  <span>Loading duplicate accounts...</span>
                </div>
              ) : duplicateModal.duplicates && duplicateModal.duplicates.length > 0 ? (
                <div className="duplicate-list">
                  {duplicateModal.duplicates.map((dup: any, idx: number) => {
                    const dupId = dup.user_id || dup.id || dup;
                    return (
                      <div key={idx} className="duplicate-item">
                        <div className="duplicate-item-info">
                          <a href={`/dashboard/users/${dupId}`} className="user-link">User #{dupId}</a>
                          {dup.match_type && <span className="flag-type">{dup.match_type}</span>}
                          {dup.similarity_score !== undefined && (
                            <span className="bk-confidence high">{(dup.similarity_score * 100).toFixed(0)}% match</span>
                          )}
                        </div>
                        <button
                          className="merge-into-btn"
                          onClick={() => setMergeModal({
                            open: true,
                            primaryId: duplicateModal.userId,
                            duplicateIds: [typeof dupId === 'number' ? dupId : parseInt(dupId)],
                            loading: false,
                          })}
                        >
                          <Merge size={14} /> Merge Into Primary
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-data" style={{ padding: '32px' }}>
                  <Users size={32} />
                  <h3>No duplicates found</h3>
                  <p>No duplicate accounts detected for this user</p>
                </div>
              )}
            </div>
            <div className="fraud-modal-footer">
              <button className="modal-cancel-btn" onClick={() => setDuplicateModal({ open: false, userId: null, duplicates: null, loading: false })}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Confirmation Modal */}
      {mergeModal.open && (
        <div className="fraud-modal-overlay" onClick={() => !mergeModal.loading && setMergeModal({ open: false, primaryId: null, duplicateIds: [], loading: false })}>
          <div className="fraud-modal" onClick={e => e.stopPropagation()}>
            <div className="fraud-modal-header danger">
              <Merge size={22} />
              <h3>Confirm Merge</h3>
              <button className="modal-close-btn" onClick={() => !mergeModal.loading && setMergeModal({ open: false, primaryId: null, duplicateIds: [], loading: false })}>
                <X size={18} />
              </button>
            </div>
            <div className="fraud-modal-body">
              <div className="ban-warning">
                <AlertCircle size={40} />
                <p>Merge <strong>{mergeModal.duplicateIds.length} duplicate account(s)</strong> into <strong>User #{mergeModal.primaryId}</strong>?</p>
                <p className="ban-warning-sub">
                  Duplicate IDs: {mergeModal.duplicateIds.map(id => `#${id}`).join(', ')}<br />
                  This action is <strong>irreversible</strong>. The duplicate accounts will be deactivated and their data merged into the primary account.
                </p>
              </div>
            </div>
            <div className="fraud-modal-footer">
              <button className="modal-cancel-btn" onClick={() => !mergeModal.loading && setMergeModal({ open: false, primaryId: null, duplicateIds: [], loading: false })} disabled={mergeModal.loading}>
                Cancel
              </button>
              <button className="modal-danger-btn" onClick={handleMergeUsers} disabled={mergeModal.loading}>
                {mergeModal.loading ? <><RefreshCw className="spin" size={16} /> Merging...</> : <><Merge size={16} /> Merge Users</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appeals Tab */}
      {activeTab === 'appeals' && (
        <div className="fraud-appeals">
          <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h2><Gavel size={20} /> Fraud Appeals</h2>
              <p className="section-sub">Users appeal restrictions here. Approving an appeal unwinds the consequence ladder — it resets the user's state, refunds any forfeited session reward, and clears freezes or account restrictions.</p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={appealStatusFilter} onChange={(e) => setAppealStatusFilter(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="all">All</option>
              </select>
              <button className="view-all" onClick={loadAppeals}><RefreshCw size={16} /> Refresh</button>
            </div>
          </div>

          {appealsLoading ? (
            <div className="loading-state"><RefreshCw className="spin" size={24} /> Loading appeals...</div>
          ) : appeals.length === 0 ? (
            <div className="empty-state"><CheckCircle size={40} /><p>No {appealStatusFilter !== 'all' ? appealStatusFilter : ''} appeals.</p></div>
          ) : (
            <table className="fraud-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Ladder State</th>
                  <th>Statement</th>
                  <th>Filed</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {appeals.map((a) => (
                  <tr key={a.id}>
                    <td>#{a.id}</td>
                    <td>{a.user_name || a.user_email || `User #${a.user_id}`}</td>
                    <td>{a.ladder_state_at_filing || '—'}</td>
                    <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{a.user_statement}</td>
                    <td>{a.created_at ? a.created_at.split('T')[0] : '—'}</td>
                    <td><span className={`status-badge status-${a.status}`}>{a.status}</span></td>
                    <td>
                      {a.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="action-btn approve" onClick={() => setAppealModal({ open: true, appeal: a, approve: true, notes: '', loading: false })}>
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button className="action-btn reject" onClick={() => setAppealModal({ open: true, appeal: a, approve: false, notes: '', loading: false })}>
                            <XCircle size={14} /> Deny
                          </button>
                        </div>
                      ) : (
                        <span className="resolved-note">{a.resolution_notes || '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Appeal resolve modal */}
      {appealModal.open && appealModal.appeal && (
        <div className="fraud-modal-overlay" onClick={() => !appealModal.loading && setAppealModal({ open: false, appeal: null, approve: true, notes: '', loading: false })}>
          <div className="fraud-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fraud-modal-header">
              <h3>{appealModal.approve ? 'Approve' : 'Deny'} Appeal #{appealModal.appeal.id}</h3>
              <button className="modal-close" onClick={() => !appealModal.loading && setAppealModal({ open: false, appeal: null, approve: true, notes: '', loading: false })}><X size={18} /></button>
            </div>
            <div className="fraud-modal-body">
              {appealModal.approve && (
                <div className="ban-warning">
                  <AlertCircle size={40} />
                  <p>Approving unwinds the ladder for <strong>{appealModal.appeal.user_name || appealModal.appeal.user_email || `User #${appealModal.appeal.user_id}`}</strong>.</p>
                  <p className="ban-warning-sub">Their ladder state resets to normal, any forfeited session reward is refunded, and any withdrawal freeze or account restriction is cleared.</p>
                </div>
              )}
              <label className="modal-label">Resolution notes {appealModal.approve ? '(optional)' : '(shown to the user)'}</label>
              <textarea
                value={appealModal.notes}
                onChange={(e) => setAppealModal((m) => ({ ...m, notes: e.target.value }))}
                placeholder={appealModal.approve ? 'Optional note for the record...' : 'Explain why this appeal is denied...'}
                rows={4}
                style={{ width: '100%' }}
              />
            </div>
            <div className="fraud-modal-footer">
              <button className="modal-cancel-btn" onClick={() => !appealModal.loading && setAppealModal({ open: false, appeal: null, approve: true, notes: '', loading: false })} disabled={appealModal.loading}>
                Cancel
              </button>
              <button
                className={appealModal.approve ? 'modal-primary-btn' : 'modal-danger-btn'}
                onClick={handleResolveAppeal}
                disabled={appealModal.loading}
              >
                {appealModal.loading ? <><RefreshCw className="spin" size={16} /> Working...</> : (appealModal.approve ? <><CheckCircle size={16} /> Approve & Unwind</> : <><XCircle size={16} /> Deny Appeal</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fraud;
